<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useMutation } from '@tanstack/vue-query'
import { login } from '@ck/auth'
import { accountsApi, ApiError } from '@ck/api-client'
import { formatCep, formatCpfCnpj, formatPhone, normalizeCep, normalizeCpfCnpj, normalizePhone } from '@ck/domain'
import { CkAlert, CkButton, CkField } from '@ck/ui'
import logoUrl from '../../design-system/logo-ck.png'
import { validateRegisterForm, type RegisterFormErrors } from './register-rules'

const form = reactive({ name: '', email: '', phone: '', cpfCnpj: '', cep: '', password: '', confirmPassword: '' })
const errors = ref<RegisterFormErrors>({})
const conflict = ref(false)
const success = ref(false)

const mutation = useMutation({
  mutationFn: () => accountsApi.register({
    name: form.name.trim(),
    email: form.email.trim(),
    phone: normalizePhone(form.phone),
    cpfCnpj: normalizeCpfCnpj(form.cpfCnpj),
    cep: normalizeCep(form.cep),
    password: form.password
  }),
  onSuccess: () => {
    success.value = true
    window.setTimeout(() => login({ loginHint: form.email.trim() }), 1200)
  },
  onError: (error: unknown) => {
    if (error instanceof ApiError) {
      conflict.value = error.problem.status === 409
      if (error.problem.errors) errors.value = error.problem.errors as RegisterFormErrors
    }
  }
})

function submit() {
  conflict.value = false
  const validation = validateRegisterForm(form)
  errors.value = validation
  if (Object.keys(validation).length > 0) return
  mutation.mutate()
}

function updatePhone(value: string) { form.phone = formatPhone(value) }
function updateCpfCnpj(value: string) { form.cpfCnpj = formatCpfCnpj(value) }
function updateCep(value: string) { form.cep = formatCep(value) }
</script>

<template>
  <div class="register">
    <div class="brand">
      <img :src="logoUrl" alt="CK Car Repair Service" />
      <h1>Criar conta</h1>
      <p class="ck-serif-italic">Portal do cliente</p>
    </div>
    <form class="card" @submit.prevent="submit">
      <CkAlert v-if="success" tone="success">Conta criada. Redirecionando para o login…</CkAlert>
      <CkAlert v-else-if="conflict" tone="error">Já existe uma conta com este e-mail. <router-link to="/login">Entrar</router-link></CkAlert>
      <CkAlert v-else-if="mutation.isError.value" tone="error">Não foi possível concluir o cadastro. Tente novamente em instantes.</CkAlert>

      <CkField
        v-model="form.name" label="Nome completo" autocomplete="name" required
        :error="errors.name?.[0]" :disabled="mutation.isPending.value || success"
      />
      <CkField
        v-model="form.email" label="E-mail" type="email" autocomplete="email" required
        :error="errors.email?.[0]" :disabled="mutation.isPending.value || success"
      />
      <CkField
        :model-value="form.phone" label="Telefone (opcional)" type="tel" autocomplete="tel"
        placeholder="+00 (00) 00000-0000" inputmode="tel" :maxlength="19" hint="Ex.: +55 (34) 99999-9999"
        :error="errors.phone?.[0]" :disabled="mutation.isPending.value || success" @update:model-value="updatePhone"
      />
      <CkField
        :model-value="form.cpfCnpj" label="CPF/CNPJ (opcional)"
        placeholder="000.000.000-00 ou 00.000.000/0000-00" inputmode="numeric" :maxlength="18"
        :error="errors.cpfCnpj?.[0]" :disabled="mutation.isPending.value || success" @update:model-value="updateCpfCnpj"
      />
      <CkField
        :model-value="form.cep" label="CEP (opcional)"
        placeholder="00000-000" inputmode="numeric" :maxlength="9"
        :error="errors.cep?.[0]" :disabled="mutation.isPending.value || success" @update:model-value="updateCep"
      />
      <CkField
        v-model="form.password" label="Senha" type="password" autocomplete="new-password" required
        hint="Ao menos 8 caracteres, incluindo um número." :error="errors.password?.[0]" :disabled="mutation.isPending.value || success"
      />
      <CkField
        v-model="form.confirmPassword" label="Confirmar senha" type="password" autocomplete="new-password" required
        :error="errors.confirmPassword?.[0]" :disabled="mutation.isPending.value || success"
      />

      <CkButton type="submit" class="cta" :disabled="mutation.isPending.value || success">
        {{ mutation.isPending.value ? 'Criando conta…' : 'Criar conta' }}
      </CkButton>
      <router-link class="back" to="/login">Já tenho conta</router-link>
    </form>
  </div>
</template>

<style scoped>
.register { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: var(--space-6); padding: var(--space-6) var(--space-4); background: var(--ck-cream-100); }
.brand { text-align: center; }
.brand img { width: 56px; height: 100px; object-fit: contain; margin: 0 auto var(--space-2); }
.brand h1 { margin: 0; font-size: var(--fs-xl); font-family: var(--font-display); font-weight: var(--fw-extrabold); color: var(--ck-text); }
.brand p { margin: var(--space-1) 0 0; color: var(--ck-maroon-700); }
.card { width: 100%; max-width: 440px; padding: var(--space-6); border: 1px solid var(--ck-cream-300); border-radius: var(--radius-xl); background: var(--ck-cream-50); box-shadow: var(--shadow-lg); }
.cta { width: 100%; margin-top: var(--space-2); }
.back { display: block; margin-top: var(--space-4); text-align: center; color: var(--ck-orange-500); font-weight: var(--fw-semibold); font-size: var(--fs-sm); text-decoration: none; }
.back:hover { text-decoration: underline; }
.back:focus-visible { outline: 2px solid var(--ck-orange-500); outline-offset: 2px; }

@media (max-width: 375px) {
  .card { padding: var(--space-4); }
}
</style>
